import { useMemo } from 'react'

import {
  matchFilterArrayIncludes,
  matchFilterNumber, sortArrayBy,
} from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import defaultRequestOptions from '@sections/Admin/packs/SystemManager/defaultRequestOptions'

import { ImageRequestOptions } from '@models/types'

const watchEntityKeys = ['images']

type ImageFilters = {
  hidden?: string,
  image_ids?: number[],
  imageable_id?: number,
  imageable_type?: string,
  limit?: number,
}

type UseImagesOptions = {
  filters?: ImageFilters,
  performHttpRequests?: boolean,
  requestOptions?: ImageRequestOptions,
}

const useImages = (options: UseImagesOptions = {}) => {
  const { filters } = options

  const {
    hidden: filterHidden,
    image_ids: filterImageIds,
    imageable_id: filterImageableId,
    imageable_type: filterImageableType,
    limit: filterLimit,
  } = filters || {}

  const {
    updatedEntities: { images: imagesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { images } = useSelector(reduxState => reduxState.entities)

  const filteredImages = useMemo(() => {
    const filtered = Object.values(images).filter((image) => {
      const {
        id, imageable_id, imageable_type, hidden,
      } = image

      const matchImageableId = matchFilterNumber(imageable_id, filterImageableId)
      const matchImageableType = filterImageableType ? imageable_type === filterImageableType : true

      const matchHidden = filterHidden ? hidden.toString() === filterHidden : true

      const matchFromImageIds = filterImageIds ? matchFilterArrayIncludes(filterImageIds, id) : true

      return matchHidden && matchImageableId && matchImageableType && matchFromImageIds
    })

    return sortArrayBy(filtered, 'asc', 'sort')
  }, [imagesUpdatedAt, JSON.stringify(filters)])

  const filteredImagesCount = filteredImages.length
  const hasFilteredImages = !!filteredImagesCount

  const loadMorePayload = useLoadMore({
    filters,
    limit: filterLimit,
    loadedCount: filteredImagesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingImages } = useReduxAction(
    'images',
    'loadImages',
    {
      limit,
      ...defaultRequestOptions.image,
      ...filtersWithOffset,
    },
    [filtersWithOffset, performHttpRequests],
    {
      // @ts-expect-error TS2339
      shouldPerformFn: ({ loading }) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredImages,
    filteredImagesCount,
    hasFilteredImages,
    loading: loadingImages,
  }
}

export type UseImagesPayload = ReturnType<typeof useImages>

export default useImages
